import { nextTick } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { apiService } from "@/services/api";
import { routerLenisRef } from "@/composables/useLenisScroll";
import { applyDocumentMeta } from "@/utils/documentMeta";
import adminRoutes from "./adminRoutes";
import publicRoutes from "./publicRoutes";

const routes = [
  ...publicRoutes,
  ...adminRoutes,
  {
    path: "/:pathMatch(.*)*",
    component: () => import("@/layouts/public/PublicLayout.vue"),
    children: [
      {
        path: "",
        name: "NotFound",
        component: () => import("@/views/public/NotFound.vue"),
        meta: { title: "페이지를 찾을 수 없습니다", robots: "noindex,follow" }
      }
    ]
  }
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    // Public 페이지에서는 Lenis가 window 스크롤을 가로채므로 Lenis의 scrollTo를 직접 호출해야 한다.
    // admin 등 Lenis 미사용 영역에서는 routerLenisRef가 undefined이므로 기본 동작을 유지.
    const lenis = routerLenisRef.value;

    if (savedPosition) {
      if (lenis) {
        lenis.scrollTo(savedPosition.top, { immediate: true, force: true });
        return false;
      }
      return savedPosition;
    }

    // 해시 딥링크는 해당 섹션으로 즉시 점프(스크롤 애니메이션 없이).
    // Lenis 활성 페이지에서는 native scroll을 Lenis가 가로채므로 lenis.scrollTo로 위임.
    if (to.hash) {
      if (lenis) {
        // 긴 문서(예: 홈)에서 짧은 페이지(예: 솔루션)로 전환하면 실제 window.scrollY는 새 문서의 최대값으로
        // 클램프되지만 Lenis 내부 animatedScroll은 한 프레임 동안 stale(이전 큰 값)로 남는다. 이 상태에서
        // lenis.scrollTo("#hash")는 elementRect.top + staleAnimatedScroll로 목표를 계산해 그 차이만큼
        // 오버슈트한다(딥링크가 의도한 카드보다 다음 카드를 보이게 됨). → 실제 window.scrollY 기준 절대
        // 위치를 직접 계산해 넘겨 stale 베이스 의존을 제거한다.
        const target = document.querySelector<HTMLElement>(to.hash);
        if (target) {
          const top = target.getBoundingClientRect().top + window.scrollY;
          lenis.scrollTo(top, { immediate: true, force: true });
        } else {
          lenis.scrollTo(to.hash, { immediate: true, force: true });
        }
        return false;
      }
      return { el: to.hash };
    }

    // 일반 navigation은 beforeEach에서 이미 0으로 초기화됐으므로 scrollBehavior는 보조 역할
    if (lenis) {
      lenis.scrollTo(0, { immediate: true, force: true });
      return false;
    }
    return { top: 0 };
  }
});

function getSafeLandingPage(authStore: ReturnType<typeof useAuthStore>): string | null {
  if (authStore.isAdmin) {
    return "/admin/company-info";
  }
  return "/";
}

// 라우트 변경 후 <head> 메타태그 동기화 (제목, description, OG, Twitter, canonical 등)
// admin 경로는 로그인 뒤 내부 페이지이므로 robots 기본값을 noindex,nofollow로 강제하고 소셜 메타는 주입하지 않음
router.afterEach((to) => {
  const isAdmin = to.path.startsWith("/admin");
  applyDocumentMeta(
    {
      title: to.meta.title as string | undefined,
      description: to.meta.description as string | undefined,
      ogTitle: to.meta.ogTitle as string | undefined,
      ogDescription: to.meta.ogDescription as string | undefined,
      ogImage: to.meta.ogImage as string | undefined,
      robots: (to.meta.robots as string | undefined) ?? (isAdmin ? "noindex,nofollow" : undefined)
    },
    to.path,
    { social: !isAdmin }
  );
});

// wayhub 내부 전환에 동일-문서 View Transitions 적용 (Chromium).
// main.css의 ::view-transition-old/new(root) + @keyframes pageEnter/pageExit(0.8s)를 그대로 재사용해
// 정적 사이트(@view-transition cross-doc)와 동일한 0.8s 클립 커튼을 재현한다.
// 비-Chromium은 즉시 전환(정적 사이트도 동일하므로 브라우저별로 정적과 일치).
type ViewTransitionDoc = Document & {
  startViewTransition?: (cb: () => void | Promise<void>) => unknown;
};

// wayhub SPA 내부 전환 판별. /wayhub-legacy(정적 비교본)는 SPA가 아니므로 제외.
const isWayhubPath = (path: string): boolean => path === "/wayhub" || path.startsWith("/wayhub/");
let vtFinish: (() => void) | null = null;

// 이 전환이 동일-문서 View Transition을 사용하는지 판별. wayhub 내부 path 전환이면서 브라우저 지원 +
// reduced-motion 비활성일 때만 VT가 작동한다. beforeEach(old 페이지 스크롤 리셋 생략)와
// beforeResolve(VT 시작)가 같은 조건을 공유하도록 헬퍼로 분리한다.
function willUseViewTransition(toPath: string, fromPath: string): boolean {
  if (!isWayhubPath(toPath) || !isWayhubPath(fromPath) || toPath === fromPath) return false;
  if (!(document as ViewTransitionDoc).startViewTransition) return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  return true;
}

router.beforeResolve((to, from) => {
  if (!willUseViewTransition(to.path, from.path)) return;
  const doc = document as ViewTransitionDoc;

  return new Promise<void>((resolve) => {
    doc.startViewTransition!(() => {
      // 네비게이션 진행 허용(컴포넌트 스왑) 후, 새 DOM 커밋(afterEach+nextTick)까지 VT가 대기하도록 promise 반환
      resolve();
      return new Promise<void>((finish) => {
        vtFinish = finish;
      });
    });
  });
});

// 새 컴포넌트 DOM 커밋 후 VT가 new 스냅샷을 찍도록 finish 해제
router.afterEach(() => {
  if (!vtFinish) return;
  const done = vtFinish;
  vtFinish = null;
  nextTick(() => done());
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  try {
    // 페이지 이동 시 모든 진행 중인 API 요청 취소 (MISSING-4: /auth/refresh는 제외됨)
    if (from.name !== undefined) {
      apiService.cancelAllRequests("Page navigation");
    }

    // Lenis 활성 페이지에서는 새 컴포넌트 렌더 전에 스크롤을 동기 초기화해야 한다
    // — Lenis RAF 기반이라 scrollBehavior 타이밍에 이전 스크롤이 한 프레임 보이는 현상 방지
    // Lenis 미사용(Admin 등)에서는 scrollBehavior가 savedPosition 복원까지 잘 동작하므로 건드리지 않는다
    // VT가 작동하는 wayhub 내부 전환에서는 여기서 old 페이지를 스크롤하면 VT old 스냅샷이 현재 위치가
    // 아닌 top으로 캡처되어 "현재 페이지 첫 화면이 번쩍이는" 문제가 생긴다. → VT 전환은 여기서 건드리지
    // 않는다. 새 페이지 위치는 scrollBehavior(savedPosition/hash/0)가 그대로 결정하며, VT new 스냅샷은
    // afterEach(nextTick)까지 지연되어 그 사이 적용된 새 스크롤로 캡처된다.
    const lenis = routerLenisRef.value;
    if (
      lenis &&
      from.name !== undefined &&
      !to.hash &&
      from.path !== to.path &&
      !willUseViewTransition(to.path, from.path)
    ) {
      lenis.scrollTo(0, { immediate: true, force: true });
      if (typeof window !== "undefined") {
        window.scrollTo(0, 0);
      }
    }

    const authStore = useAuthStore();

    // ensureAuthResolved: 최초 1회만 서버에 세션 복원 시도 (localStorage 직접 접근 제거)
    await authStore.ensureAuthResolved();

    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      // 관리자 경로면 관리자 로그인으로, 아니면 공개 로그인으로
      const loginPath = to.path.startsWith("/admin") ? "/admin/login" : "/login";
      next({ path: loginPath, query: { redirect: to.fullPath } });
      return;
    }

    // Check role-based access
    if (to.meta.requiresAdmin && !authStore.isAdmin) {
      next({ path: "/admin/login", query: { redirect: to.fullPath } });
      return;
    }

    // Check if route requires guest (not authenticated)
    if (to.meta.requiresGuest && authStore.isAuthenticated) {
      const landingPage = getSafeLandingPage(authStore);
      if (landingPage) {
        next(landingPage);
      } else {
        authStore.clearSessionState();
        next();
      }
      return;
    }

    next();
  } catch (error) {
    console.error("Router navigation error:", error);
    next("/");
  }
});
